import { z } from 'zod';
import { BillingAddressSchema, CrmContactSchema, VatIdValidationSchema } from '../../../../entities/account/account.schema';
/**
 * One customer in the super-admin account list: who they are commercially, what
 * their Bliks access currently is, and how many workspaces they run.
 *
 * The company fields are read live from HubSpot rather than stored — HubSpot
 * owns the name and a cached copy would drift (LIC-06). They are nullable
 * because an account can exist before anyone wrote its company id down, and
 * because a company id can outlive the record it points at.
 */ export const AccountListItemSchema = z.object({
    _id: z.string(),
    hubspotCompanyId: z.string().nullable(),
    name: z.string().nullable(),
    domain: z.string().nullable(),
    /** How many workspaces this customer runs — a consultancy runs two. */ tenantCount: z.number().int().nonnegative(),
    /** The account this one belongs to, for the group column (LIC-09). */ parent: z.object({
        _id: z.string(),
        name: z.string().nullable()
    }).nullable(),
    /** How many accounts sit under this one — the group's size at a glance. */ childCount: z.number().int().nonnegative(),
    /**
   * Ob die Verwalter der Arbeitsbereiche die Abrechnungsdaten selbst ändern
   * dürfen. Abwesend heißt ja — das ist die Regel (Issue 33), und der
   * Schalter ist die Ausnahme.
   */ billingSelfService: z.boolean().optional(),
    /**
   * Der Hauptansprechpartner aus dem CRM — die Person, an die ein Angebot
   * geht. `null`, solange die Firma keinen so gekennzeichneten Kontakt hat.
   */ primaryContact: CrmContactSchema.nullable(),
    /**
   * Der Abrechnungskontakt aus dem CRM — die Person, die die Rechnung
   * erwartet. Oft ein anderer als der, der über den Kauf entscheidet.
   */ billingContact: CrmContactSchema.nullable(),
    /**
   * Wohin die Rechnung geht — die Adresse, die der Anbieter bekommt.
   *
   * Aus dem CRM gelesen und dort geändert (LIC-26). Steht in der Liste,
   * weil der Konfigurator sie **zeigen** muss: wer ein Angebot ausstellt,
   * soll sehen, wer die Rechnung erhält, ohne dafür die Kontoseite
   * aufzuschlagen. Besitzen tut sie diese Seite nicht.
   */ billingEmail: z.string().nullable(),
    /**
   * Die Rechnungsanschrift, wie das CRM sie führt (LIC-26).
   *
   * Sie steht neben dem Rechnungsempfänger und aus demselben Grund: an ihr
   * hängt der Steuersatz. Fehlt sie, rechnet der Anbieter nach seinem
   * eigenen Sitz ab — dänische 25 % auf eine deutsche Rechnung —, und wer
   * ein Angebot ausstellt, muss das vorher sehen können.
   */ billingAddress: BillingAddressSchema.nullable(),
    /** Die USt-IdNr. aus dem CRM; Grundlage für Reverse Charge. */ vatId: z.string().nullable(),
    /**
   * Was die letzte Prüfung dieser Nummer ergeben hat; `null`, solange keine
   * lief. Eine ungeprüfte Nummer sieht sonst aus wie eine bestätigte, und
   * nur die bestätigte geht an den Anbieter.
   */ vatIdValidation: VatIdValidationSchema.nullable()
});
export const ListAccountsResponseSchema = z.object({
    accounts: z.array(AccountListItemSchema),
    /**
   * False when HubSpot is not configured or unreachable. The list still renders
   * from our own records; only the company names are missing, and the UI has to
   * say so rather than show every account as nameless.
   */ companiesResolved: z.boolean()
});

//# sourceMappingURL=list-accounts.schema.js.map